import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
} from 'recharts';
import { useYahooHistory } from '@/hooks/useYahooQuote';
import { toPercentReturns } from '@/lib/yahooFinanceService';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle } from 'lucide-react';
import type { YahooRange } from '@/lib/yahooFinanceService';

interface Props {
  ticker: string;
  benchmarkSymbol?: string;   // e.g. 'SPY', 'QQQ'
  range?: YahooRange;
  height?: number;
}

const RANGE_LABELS: Record<YahooRange, string> = {
  '1mo': '1 Month', '3mo': '3 Months', '6mo': '6 Months',
  '1y': '1 Year', '2y': '2 Years', '5y': '5 Years',
  '10y': '10 Years', 'ytd': 'YTD', 'max': 'Max',
};

const TICKER_COLOR = '#3b82f6';
const BENCHMARK_COLOR = '#94a3b8';

export function BenchmarkChart({
  ticker,
  benchmarkSymbol = 'SPY',
  range = '5y',
  height = 300,
}: Props) {
  const { data: tickerHistory, isLoading: loadingTicker, error: errorTicker } = useYahooHistory(ticker, '1mo', range);
  const { data: benchHistory, isLoading: loadingBench } = useYahooHistory(benchmarkSymbol, '1mo', range);

  if (loadingTicker || loadingBench) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-4 w-48" />
        <Skeleton style={{ height }} className="w-full rounded-lg" />
      </div>
    );
  }

  if (errorTicker || !tickerHistory?.length) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
        <AlertCircle className="w-4 h-4" />
        <span>Price history unavailable for {ticker}</span>
      </div>
    );
  }

  // Align both series on shared dates
  const tickerReturns = toPercentReturns(tickerHistory);
  const benchReturns = benchHistory?.length ? toPercentReturns(benchHistory) : [];

  const benchMap = new Map(benchReturns.map((p) => [p.date, p.return]));

  const chartData = tickerReturns.map((p) => ({
    date: p.date.slice(0, 7), // YYYY-MM
    [ticker]: parseFloat(p.return.toFixed(2)),
    [benchmarkSymbol]: benchMap.has(p.date)
      ? parseFloat((benchMap.get(p.date) ?? 0).toFixed(2))
      : null,
  }));

  const CustomTooltip = ({ active, payload, label }: {
    active?: boolean;
    payload?: Array<{ name: string; value: number; color: string }>;
    label?: string;
  }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-card border border-border rounded-lg p-3 shadow-lg text-sm">
        <p className="font-semibold text-foreground mb-1">{label}</p>
        {payload.map((entry) => entry.value !== null && (
          <p key={entry.name} style={{ color: entry.color }}>
            {entry.name}: {entry.value >= 0 ? '+' : ''}{entry.value.toFixed(1)}%
          </p>
        ))}
      </div>
    );
  };

  return (
    <div className="w-full">
      <h3 className="text-sm font-semibold text-muted-foreground mb-1 uppercase tracking-wide">
        Price Performance — {RANGE_LABELS[range]}
      </h3>
      <p className="text-xs text-muted-foreground mb-3">
        % return since start of period · vs {benchmarkSymbol} · via Yahoo Finance
      </p>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={chartData} margin={{ top: 4, right: 16, bottom: 4, left: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.5} />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
            axisLine={false}
            tickLine={false}
            interval="preserveStartEnd"
          />
          <YAxis
            tickFormatter={(v) => `${v >= 0 ? '+' : ''}${v.toFixed(0)}%`}
            tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
            axisLine={false}
            tickLine={false}
            width={52}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }} />
          <ReferenceLine y={0} stroke="hsl(var(--border))" strokeWidth={1} />
          <Line
            type="monotone"
            dataKey={ticker}
            stroke={TICKER_COLOR}
            strokeWidth={2}
            dot={false}
            connectNulls
          />
          {benchHistory?.length ? (
            <Line
              type="monotone"
              dataKey={benchmarkSymbol}
              stroke={BENCHMARK_COLOR}
              strokeWidth={1.5}
              strokeDasharray="4 2"
              dot={false}
              connectNulls
            />
          ) : null}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
